/**
 * Phase 9 — financial math helpers.
 *
 * Pure functions that:
 *   • Client-side: feed the Finance tab cells + project-level summary header.
 *   • Server-side: feed the `onTimeEntryWrite` Cloud Function which
 *     recomputes `hoursSpent`, `remainingHours`, `percentCompletion` on the
 *     affected `Designation` doc.
 *
 * Dependency-free — no React, no Firestore SDK (Timestamp is a pure
 * data class).
 *
 * Number policy:
 *   • hours are stored rounded to 2 decimals — diff-friendly writes,
 *     avoids float drift when echoed back from Firestore.
 *   • percentCompletion is rounded to 1 decimal — reads well in a cell.
 *   • All outputs are clamped to [0, ∞) for hours and [0, 100] for
 *     percent; the UI must never render NaN or a progress bar > track.
 */

import type { Designation, TimeEntry } from '@altyor-kronos/shared-types';

// ─── Rounding helpers ─────────────────────────────────────────────

const round2 = (n: number) => Math.round(n * 100) / 100;
const round1 = (n: number) => Math.round(n * 10) / 10;

// ─── Core math ────────────────────────────────────────────────────

/**
 * Sum all matching entries' durationSeconds into hours, rounded to 2
 * decimals. Callers pass the full entries array — Firestore query
 * selectivity happens at the subscription layer.
 */
export function computeHoursSpent(
  entries: readonly TimeEntry[],
  projectId: string,
  designationId: string,
): number {
  let totalSeconds = 0;
  for (const e of entries) {
    if (e.projectId !== projectId || e.designationId !== designationId) continue;
    totalSeconds += e.durationSeconds ?? 0;
  }
  return round2(totalSeconds / 3600);
}

/**
 * `max(hoursSold - hoursSpent, 0)` rounded to 2 decimals. Never negative.
 * When `hoursSold == 0`, the remaining is also 0 (we don't want to imply
 * the team is "ahead" on a designation with no budget).
 */
export function computeRemainingHours(hoursSold: number, hoursSpent: number): number {
  if (!Number.isFinite(hoursSold) || hoursSold <= 0) return 0;
  const delta = hoursSold - Math.max(hoursSpent, 0);
  return round2(Math.max(delta, 0));
}

/**
 * `(hoursSpent / hoursSold) × 100`, rounded to 1 decimal, clamped to
 * [0, 100]. Returns 0 when `hoursSold <= 0` so cells never display
 * NaN or Infinity.
 */
export function computePercentCompletion(hoursSold: number, hoursSpent: number): number {
  if (!Number.isFinite(hoursSold) || hoursSold <= 0) return 0;
  const safeSpent = Math.max(hoursSpent, 0);
  const raw = (safeSpent / hoursSold) * 100;
  if (!Number.isFinite(raw)) return 0;
  return round1(Math.min(raw, 100));
}

// ─── Currency formatter ──────────────────────────────────────────

/**
 * EUR formatter driven by the viewer's locale. Intl.NumberFormat gives
 * us the grouping + decimal + symbol in one call; locale selection is
 * delegated to react-i18next in the UI layer.
 */
export function formatCurrencyEUR(amount: number, locale: string): string {
  const value = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Plain number formatter — "—" for missing/non-finite values. Shared
 * by the Finance list, KPI bar and detail panel.
 */
export function formatNumber(n: number | undefined, locale: string): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(n);
}

// ─── Project-level summary ───────────────────────────────────────

/** Row shape consumed by the summary — matches `Designation`. */
export type DesignationRow = Designation;

export interface ProjectFinanceSummary {
  /** Sum of `orderedAmount` over every designation — the "TOTAL TURNOVER" cell. */
  totalTurnover: number;
  /** Sum of `initialOffer` — the "TOTAL INITIAL OFFER" cell. */
  totalInitialOffer: number;
  /** Sum of `hoursSold`. */
  totalHoursSold: number;
  /** Sum of `hoursSpent`. */
  totalHoursSpent: number;
  /** Aggregate completion in the same sense as the per-row cell. */
  percentCompletion: number;
}

/**
 * Reduce an array of `Designation` rows into a project-level summary
 * identical in shape to the spec §13 header ("TOTAL TURNOVER",
 * "TOTAL R&D", etc.). Keeping it pure lets the Finance tab + its
 * tests + any future scheduled reporting share one implementation.
 */
export function projectSummary(rows: readonly DesignationRow[]): ProjectFinanceSummary {
  let totalTurnover = 0;
  let totalInitialOffer = 0;
  let totalHoursSold = 0;
  let totalHoursSpent = 0;
  for (const r of rows) {
    totalTurnover += r.orderedAmount ?? 0;
    totalInitialOffer += r.initialOffer ?? 0;
    // `hoursSold` and `hoursSpent` do NOT have the same scope. A fixed-price
    // row has no hour budget, so it is skipped for `hoursSold`. But time can
    // really be logged against it (p26014 carries 7.78 h on three `fixed`
    // lines in production), and those hours must be counted or the project
    // total silently under-reports real work.
    if (r.billingMode !== 'fixed') {
      totalHoursSold += r.hoursSold ?? 0;
    }
    totalHoursSpent += r.hoursSpent ?? 0;
  }
  const percentCompletion = computePercentCompletion(totalHoursSold, totalHoursSpent);
  return {
    totalTurnover: round2(totalTurnover),
    totalInitialOffer: round2(totalInitialOffer),
    totalHoursSold: round2(totalHoursSold),
    totalHoursSpent: round2(totalHoursSpent),
    percentCompletion,
  };
}
